import { BookIcon, BooksIcon, CheckIcon, MapPinIcon, TranslateIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LoadingSpinner from "../../../basic/loadingSpinner/LoadingSpinner";
import StyledButton from "../../../basic/styledButton/StyledButton";
import { FEDERAL_STATES, NO_FEDERAL_STATE, type FederalStateChoice } from "../../../constants/FederalStates";
import { useBookContext } from "../../../context/BookContext";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import type { CatalogBook } from "../../../helpers/BookCatalog";
import { isCompleteIsbn } from "../../../helpers/Isbn";
import { useConfirmBook } from "../../../hooks/useConfirmBook";
import { useLogger } from "../../../hooks/useLogger";
import { getBookCatalog, type BookCatalog, type BookCatalogVolume } from "../../../networking/bookCatalog";
import BookSearchField from "./BookSearchField";
import BookSearchResults from "./BookSearchResults";
import BookSelectionStepper from "./BookSelectionStepper";
import BookStep from "./BookStep";
import FederalStateFlag from "./FederalStateFlag";
import FederalStateStep from "./FederalStateStep";
import LanguageFlag from "./LanguageFlag";
import LanguageStep from "./LanguageStep";
import { useBookSelectionSteps, type BookSelectionStep, type IsbnLookupResult } from "./useBookSelectionSteps";
import { useStepHistory } from "./useStepHistory";
import VolumeStep from "./VolumeStep";

interface BookSelectionProps {
    /** The page's footer element, which the next/confirm button renders into, so it can't scroll out of view. */
    confirmButtonSlot: HTMLElement | null;
}

/**
 * Federal state → language → book → volume, from the book catalog. The catalog only drives the choice:
 * the confirmed volume is resolved via get-band-info, so the creation flow keeps working with p6u's same
 * band, book and subject objects as before.
 */
const BookSelection = (props: BookSelectionProps) => {
    const { selectedBookData, loadInitArticleIdData, openWarningModal } = useTestCreationContext();
    const takeOverBook = useConfirmBook();
    const { bookFilters, isBookBlocked } = useBookContext();
    const { logEvent } = useLogger();

    const t = useT();
    const t_federalState = t("Federal state", { _tags: "CreateTest,SelectBook" });
    const t_noFederalState = t("No federal state", { _tags: "CreateTest,SelectBook" });
    const t_language = t("Language", { _tags: "CreateTest,SelectBook" });
    const t_textbook = t("Textbook", { _tags: "CreateTest,SelectBook" });
    const t_volume = t("Volume", { _tags: "CreateTest,SelectBook" });
    const t_next = t("Next", { _tags: "CreateTest,SelectBook" });
    const t_confirm = t("Use book", { _tags: "CreateTest,SelectBook" });
    const t_stepByStep = t("or find it step by step", { _tags: "CreateTest,SelectBook" });
    const t_allBooksHint = t("Without a federal state, all textbooks are shown.", { _tags: "CreateTest,SelectBook" });
    const t_errorLoadingBookInfo = t("There was an error while loading book information.", {
        _tags: "CreateTest,SelectExerciseType",
    });
    const t_bookNotAvailable = t("This book is not available for this activity.", {
        _tags: "CreateTest,SelectBook",
    });

    const [catalog, setCatalog] = useState<BookCatalog>();
    const [isConfirming, setIsConfirming] = useState(false);
    const [isbnLookup, setIsbnLookup] = useState<IsbnLookupResult>();

    useEffect(() => {
        // A failed load is handled by SelectBookExperiment, which falls back to the dropdowns.
        getBookCatalog()
            .then(setCatalog)
            .catch(() => undefined);
    }, []);

    // get-band-info delivers the band id as a number, despite its type.
    const selectedArticleId = selectedBookData?.band?.ID;
    const steps = useBookSelectionSteps({
        catalog: catalog?.volumes,
        filters: bookFilters,
        initialArticleId: selectedArticleId !== undefined ? String(selectedArticleId) : undefined,
    });

    const containerRef = useRef<HTMLDivElement>(null);
    // Each step the teacher opens starts at its top, but not the one restored on load. Instant, as a smooth
    // scroll stops short while the next step's shorter content replaces the last one.
    const scrollToStepTop = () => containerRef.current?.scrollIntoView({ block: "start" });

    const openStepInHistory = useStepHistory({
        step: steps.step,
        onStepRestore: (step) => {
            // Back leaves a search as well, so the restored step shows instead of the results.
            steps.setSearchQuery("");
            setIsbnLookup(undefined);
            // A step that has closed since (e.g. another book was picked) falls back to the last one still open.
            let target = step;
            while (target > 1 && !steps.isStepReachable(target)) {
                target = (target - 1) as BookSelectionStep;
            }
            steps.goToStep(target);
            scrollToStepTop();
        },
    });
    const openStep = (step: BookSelectionStep) => {
        openStepInHistory(step);
        scrollToStepTop();
    };

    const federalStateName =
        steps.federalState === NO_FEDERAL_STATE
            ? t_noFederalState
            : FEDERAL_STATES.find((state) => state.code === steps.federalState)?.name;
    const languageName = steps.language?.subjectName ?? steps.book?.subjectName ?? "";

    // Without the states' approvals every book is shown, whatever state was picked.
    const isFilteredByState =
        steps.federalState !== NO_FEDERAL_STATE && !!federalStateName && !!catalog?.hasFederalStates;

    const getApprovalHint = () => {
        if (!isFilteredByState) {
            return t_allBooksHint;
        }
        return steps.isFederalStateRemembered
            ? t("Your saved federal state: {state}. Only the textbooks approved there are shown.", {
                  _tags: "CreateTest,SelectBook",
                  state: federalStateName,
              })
            : t("Showing the textbooks approved in {state}.", {
                  _tags: "CreateTest,SelectBook",
                  state: federalStateName,
              });
    };

    const handleFederalStatePick = (choice: FederalStateChoice) => {
        steps.pickFederalState(choice);
        openStep(2);
        logEvent("selected federal state", {
            federal_state: choice,
            is_remember_checked: steps.shouldRememberFederalState,
        });
    };

    const handleSearchChange = (query: string) => {
        steps.setSearchQuery(query);
        setIsbnLookup(undefined);
        // A complete ISBN names the volume exactly, so it skips straight to it.
        if (isCompleteIsbn(query)) {
            const result = steps.pickVolumeByIsbn(query);
            setIsbnLookup(result);
            if (result === "picked") {
                openStep(4);
            }
        }
    };

    const handleLanguagePick = (subjectId: string) => {
        steps.pickLanguage(subjectId);
        openStep(3);
        logEvent("Subject Selected", {
            subject: steps.languages.find((language) => language.subjectId === subjectId)?.subjectName,
            subject_id: subjectId,
        });
    };

    const handleBookPick = (book: CatalogBook) => {
        steps.pickBook(book);
        openStep(4);
        logEvent("Book Selected", { shop_book_id: book.id, publisher: book.publisherName });
    };

    const handleVolumePick = (volume: BookCatalogVolume) => {
        steps.pickVolume(volume);
        logEvent("Band Selected", { band: volume.name, article_id: volume.articleId });
    };

    const confirmBook = async () => {
        if (!steps.volume) {
            return;
        }

        setIsConfirming(true);
        try {
            const bandInfo = await loadInitArticleIdData(steps.volume.articleId, false, false);
            if (!bandInfo.band) {
                throw new Error("No band info was loaded");
            }
            // Backstop for the catalog's filters, which only see the catalog's ids.
            if (isBookBlocked(bandInfo)) {
                openWarningModal(t_bookNotAvailable);
                setIsConfirming(false);
                return;
            }
            takeOverBook(bandInfo, {
                federal_state: steps.federalState,
                is_federal_state_preselected: steps.isFederalStateRemembered,
            });
        } catch {
            openWarningModal(t_errorLoadingBookInfo);
            setIsConfirming(false);
        }
    };

    const isCurrentStepDone = [!!steps.federalState, !!steps.language, !!steps.book, !!steps.volume][steps.step - 1];

    const goToNextStep = () => {
        const nextStep = (steps.step + 1) as BookSelectionStep;
        steps.goToStep(nextStep);
        openStep(nextStep);
    };

    // A found ISBN stays in the field, with its volume below instead of results.
    const isSearching = !!steps.searchQuery.trim() && isbnLookup !== "picked";

    const renderActiveStep = () => {
        if (isSearching) {
            return (
                <BookSearchResults
                    query={steps.searchQuery}
                    // The search is filtered by state too, so say so like the textbook step.
                    approvalHint={isFilteredByState ? getApprovalHint() : undefined}
                    books={steps.searchResults}
                    isbnLookup={isbnLookup}
                    selectedBookId={steps.book?.id}
                    onPick={handleBookPick}
                />
            );
        }
        if (steps.step === 4 && steps.book) {
            return (
                <VolumeStep
                    book={steps.book}
                    volume={steps.volume}
                    onPick={handleVolumePick}
                />
            );
        }
        if (steps.step === 3 && steps.language) {
            return (
                <BookStep
                    languageName={languageName}
                    approvalHint={getApprovalHint()}
                    books={steps.books}
                    selectedBookId={steps.book?.id}
                    onPick={handleBookPick}
                />
            );
        }
        if (steps.step === 2 && steps.federalState) {
            return (
                <LanguageStep
                    languages={steps.languages}
                    selectedSubjectId={steps.language?.subjectId}
                    onPick={handleLanguagePick}
                />
            );
        }
        return (
            <FederalStateStep
                federalState={steps.federalState}
                shouldRemember={steps.shouldRememberFederalState}
                onRememberChange={steps.setShouldRememberFederalState}
                onPick={handleFederalStatePick}
            />
        );
    };

    if (!catalog) {
        return (
            <div className="flex justify-center py-xl">
                <LoadingSpinner colorClass="border-p6-grey-lightest border-t-creation-primary" />
            </div>
        );
    }

    // The volume's short number for the stepper's compact marker ("Band 5 (Abschluss)" → "5"), not a year.
    const volumeNumber = steps.volume?.name.match(/\b\d{1,2}\b/)?.[0];

    return (
        <div
            ref={containerRef}
            className="flex scroll-mt-[var(--size-book-selection-scroll-offset)] flex-col gap-m"
            data-testid="book-selection"
        >
            <div className="flex flex-col gap-l">
                <BookSearchField
                    query={steps.searchQuery}
                    onQueryChange={handleSearchChange}
                />
                {/* Hidden while search results replace the steps. */}
                {!isSearching && (
                    <>
                        <div className="flex items-center gap-m">
                            <span className="h-px flex-1 bg-p6-grey-mid" />
                            <span className="whitespace-nowrap text-m text-p6-grey-dark">{t_stepByStep}</span>
                            <span className="h-px flex-1 bg-p6-grey-mid" />
                        </div>
                        <BookSelectionStepper
                            items={[
                                {
                                    step: 1,
                                    label: federalStateName ?? t_federalState,
                                    isDone: !!steps.federalState,
                                    isReachable: steps.isStepReachable(1),
                                    icon: <MapPinIcon />,
                                    pickedIcon: steps.federalState && (
                                        <FederalStateFlag
                                            choice={steps.federalState}
                                            className="size-full"
                                        />
                                    ),
                                },
                                {
                                    step: 2,
                                    label: languageName || t_language,
                                    isDone: !!languageName,
                                    isReachable: steps.isStepReachable(2),
                                    icon: <TranslateIcon />,
                                    pickedIcon: languageName && <LanguageFlag languageName={languageName} />,
                                },
                                {
                                    step: 3,
                                    label: steps.book?.bookName ?? t_textbook,
                                    isDone: !!steps.book,
                                    isReachable: steps.isStepReachable(3),
                                    icon: <BookIcon />,
                                    pickedIcon:
                                        steps.book &&
                                        (steps.book.coverImage ? (
                                            <img
                                                src={steps.book.coverImage}
                                                alt=""
                                                // From the top, where covers carry their title; the middle is often blank.
                                                className="size-full object-cover object-top"
                                            />
                                        ) : (
                                            <CheckIcon weight="bold" />
                                        )),
                                },
                                {
                                    step: 4,
                                    label: steps.volume?.name ?? t_volume,
                                    isDone: !!steps.volume,
                                    isReachable: steps.isStepReachable(4),
                                    icon: <BooksIcon />,
                                    pickedIcon: steps.volume && (volumeNumber ?? <CheckIcon weight="bold" />),
                                },
                            ]}
                            activeStep={steps.step}
                            onStepClick={(step) => {
                                steps.goToStep(step);
                                openStep(step);
                            }}
                        />
                    </>
                )}
            </div>
            {renderActiveStep()}
            {/* "Next" until the volume step, so a teacher who went back goes on with one click, whatever the step. */}
            {props.confirmButtonSlot &&
                createPortal(
                    <StyledButton
                        buttonStyle="PRIMARY"
                        arrowDirection="RIGHT"
                        textStyle="NO_TRANSFORMATION"
                        // The vocab training's footer lets button labels wrap; this one stays on a single line.
                        className="[&>div]:whitespace-nowrap!"
                        // Disabled while search results replace the steps.
                        disabled={isSearching || !isCurrentStepDone || isConfirming}
                        onClick={steps.step === 4 ? confirmBook : goToNextStep}
                        data-testid={steps.step === 4 ? "button-select-book" : "button-next-step"}
                    >
                        {steps.step === 4 ? t_confirm : t_next}
                    </StyledButton>,
                    props.confirmButtonSlot
                )}
        </div>
    );
};

export default BookSelection;
